import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';

import { BuyerService } from '../../../core/api/buyer.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { Cohort, Site } from '../../../shared/models';
import * as fromApp from '../../../store';
import { selectSelectedStands } from '../../../store/parcel/parcel.selectors';

@Component({
  selector: 'fc-buyer-card',
  templateUrl: './buyer-card.component.html',
  styleUrls: ['./buyer-card.component.scss'],
})
export class BuyerCardComponent implements OnInit {
  @Input() site: Site;
  @Input() cohort: Cohort;
  @Input() selectedCohort: Cohort;

  @Output() buyerSelected: EventEmitter<Cohort> = new EventEmitter<Cohort>();

  destroyRef = inject(DestroyRef);

  twentyYearMark: string = null;
  twentyYearProjection: number = 0;
  simulationPercent: number = 0;
  selectedStandList: any = [];
  baseValuePerYear: number = 0;
  averageValuePerYear: number = 0;
  fixedAnnualPayment: number = 0;
  totalFixedPayment: number = null;

  showSimulation = false;
  todayYear = new Date().getFullYear();
  yearsIntoSite: number = 0;

  getOpenAssetURL = BuyerService.getOpenAssetURL;

  constructor(
    readonly store: Store<fromApp.AppState>,
    public siteHelper: SiteHelperService,
    public formatHelper: FormatHelperService
  ) {}

  newSliderValue(value: number) {
    let totalValue = this.totalFixedPayment;
    let currentPaymentPeriod = this.cohort.initialFixedYears * (12 / this.cohort.payPeriodCadence) + 1;
    for (
      currentPaymentPeriod;
      currentPaymentPeriod <= this.site.getTotalPaymentPeriods(this.cohort);
      currentPaymentPeriod++
    ) {
      const paymentYear = Math.ceil(currentPaymentPeriod / (12 / this.cohort.payPeriodCadence));
      const newGrowth = Math.pow(value / 100 + 1, paymentYear - 1);
      totalValue += this.baseValuePerYear * newGrowth * (this.cohort.payPeriodCadence / 12);
    }
    this.twentyYearProjection = totalValue;
    this.averageValuePerYear = this.twentyYearProjection / this.yearsIntoSite;
  }

  ngOnInit(): void {
    this.showSimulation = this.cohort.initialFixedYears < 20;
    this.twentyYearMark = this.site.getYearMarkString(0, this.cohort.id);
    this.yearsIntoSite = this.site.getYearsLeftInSite(this.cohort);
    this.todayYear = new Date(this.cohort.startDate + 'T00:00:00').getFullYear();
    this.store
      .select(selectSelectedStands)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(stands => (this.selectedStandList = stands));

    this.baseValuePerYear = this.site.getEstimatedBasePayment(
      this.cohort.id,
      this.site.periodicReports[0] ? this.site.periodicReports[0].isSmallClearing : false
    );
    this.totalFixedPayment = this.site.getTotalFixedYearPayment(this.cohort.id);
    this.fixedAnnualPayment =
      this.site.getTotalFixedYearPayment(this.cohort.id, true) / (12 / this.cohort.payPeriodCadence);

    this.twentyYearProjection = this.site.getTwentyYearProjection(this.cohort.id)[0];
    this.averageValuePerYear = this.twentyYearProjection / this.yearsIntoSite;
    if (this.showSimulation) {
      this.newSliderValue(this.simulationPercent);
    }
  }

  projectionInfoBox(year: string, includeMarketPerformance: boolean = false): string {
    if (this.site.cohort?.region) {
      return this.site.cohort.region.projectionInfoText(year, includeMarketPerformance);
    }
    return '';
  }

  onBuyerCardClick(): void {
    this.buyerSelected.emit(this.cohort);
  }

  get buyerCardClass() {
    if (this.cohort === this.selectedCohort) {
      return 'selected';
    }
    return '';
  }

  get paymentFrequency() {
    switch (this.cohort.payPeriodCadence) {
      case 12:
        return 'Annually';
      case 6:
        return 'Semi-Annually';
      case 3:
        return 'Quarterly';
      case 1:
        return 'Monthly';
      default:
        return 'Unknown';
    }
  }
}
